// The date register: what is coming, what was missed, and the service history
// behind it.
//
// The point of R6 is that an engagement which changes by silence is a governance
// failure. So a lapsed date is rendered loudly and is never quietly dropped, and
// the exceptions list exists so a deviation is as visible as the standard it
// departed from.
import { useId, useState, type CSSProperties } from 'react';
import { Badge, Button, Checkbox, Mono, Avatar, Select, type ToneName } from './primitives';
import { PCard } from './people';
import { DisclosedForm, PeriodStepper } from './card';
import { DateField, Field, TextInput } from './field';
import { InlineStatus, useToast } from './feedback';
import { fmtDate } from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import {
  addKeyDate,
  actionKeyDate,
  addEvent,
  dateTypeLabel,
  DATE_TYPES,
  type KeyDate,
  type DueDate,
  type EngagementEvent,
  type HrException,
  type DateType,
} from '../lib/keyDates';

// A date's state, in words a person uses. The column says pending / actioned /
// lapsed; "pending" in amber read as waiting on someone, and a date still ahead
// is not a to-do (UI-CONVENTIONS §1). Rose is for the one that was missed (§3).
const STATE: Record<KeyDate['status'], { tone: ToneName; text: string }> = {
  pending: { tone: 'neutral', text: 'upcoming' },
  actioned: { tone: 'success', text: 'done' },
  lapsed: { tone: 'danger', text: 'overdue' },
};

const ROW: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'var(--hf-space-2) var(--hf-space-3)',
  padding: 'var(--hf-space-2) 0',
  borderBottom: '1px solid var(--hf-rule)',
  fontSize: 'var(--hf-text-sm)',
};
const NOTE: CSSProperties = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' };
const FORM: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--hf-space-3)',
  marginTop: 'var(--hf-space-4)',
};
const FIELDS: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-3)' };
const ACTIONS: CSSProperties = { display: 'flex', gap: 'var(--hf-space-2)' };

/** HR's queue of dates that have lapsed or are coming up. */
export function DueDatesCard({ rows, onChanged }: { rows: DueDate[]; onChanged: () => void }) {
  const toast = useToast();
  const { dateFormat } = useOrgDates();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (!rows.length) return null;

  const action = async (r: DueDate) => {
    setBusy(r.id);
    setErr(null);
    const { error } = await actionKeyDate(r.id);
    if (error) setErr(error.message);
    else
      toast.push({
        tone: 'success',
        message: `Marked ${r.full_name ?? 'their'} ${dateTypeLabel(r.date_type).toLowerCase()} done.`,
      });
    onChanged();
    setBusy(null);
  };

  const lapsed = rows.filter((r) => r.status === 'lapsed').length;

  return (
    <PCard
      label="Dates needing attention"
      // A facet of the list below, dimmed: the tab's AttentionCount is the one
      // total for this queue (UI-CONVENTIONS §1). Each missed date says so itself.
      right={lapsed > 0 && <Mono dim>{lapsed} overdue</Mono>}
    >
      {err && (
        <div style={{ marginBottom: 'var(--hf-space-2)' }}>
          <InlineStatus tone="error">Couldn’t mark it done — {err}</InlineStatus>
        </div>
      )}

      {rows.map((r) => (
        <div key={r.id} style={ROW}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--hf-space-2)',
              flex: '1 1 10rem',
              minWidth: 0,
            }}
          >
            <Avatar name={r.full_name ?? undefined} size={26} />
            <span style={{ color: 'var(--hf-ink)', overflowWrap: 'anywhere' }}>
              {r.full_name ?? 'Someone'}
            </span>
          </span>
          <span
            style={{
              display: 'inline-flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--hf-space-2)',
              flex: '2 1 14rem',
              minWidth: 0,
            }}
          >
            <span style={{ color: 'var(--hf-ink-2)' }}>{dateTypeLabel(r.date_type)}</span>
            <Mono>{fmtDate(r.date, dateFormat)}</Mono>
            <Badge tone={STATE[r.status].tone} dot={r.status === 'lapsed'}>
              {STATE[r.status].text}
            </Badge>
          </span>
          <Button
            variant="accent"
            size="sm"
            icon="check"
            loading={busy === r.id}
            onClick={() => void action(r)}
          >
            Mark done
          </Button>
        </div>
      ))}
    </PCard>
  );
}

/** The dates held about one person, on their record. */
export function KeyDatesCard({
  profileId,
  rows,
  canEdit,
  onChanged,
}: {
  profileId: string;
  rows: KeyDate[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const { dateFormat } = useOrgDates();
  // The card's job is the list. Its add form opens on demand: it used to sit
  // under a one-line empty state on every record (HRP-18).
  const [adding, setAdding] = useState(false);
  const opener = useId();
  const [type, setType] = useState<DateType>('contract_end');
  const [date, setDate] = useState('');
  const [dateErr, setDateErr] = useState<string>();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setAdding(false);
    setDate('');
    setDateErr(undefined);
    setErr(null);
  };

  const add = async () => {
    if (!date) {
      setDateErr('Pick a date.');
      return;
    }
    setDateErr(undefined);
    setBusy(true);
    setErr(null);
    const { error } = await addKeyDate({ profileId, dateType: type, date });
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    toast.push({
      tone: 'success',
      message: `Added ${dateTypeLabel(type).toLowerCase()} on ${fmtDate(date, dateFormat)}.`,
    });
    close();
    onChanged();
  };

  return (
    <PCard
      label="Key dates"
      right={
        canEdit &&
        !adding && (
          <span id={opener}>
            <Button variant="secondary" size="sm" icon="plus" onClick={() => setAdding(true)}>
              Add date
            </Button>
          </span>
        )
      }
    >
      {!rows.length && (
        <div style={NOTE}>
          No dates yet. A probation end appears here once a join date is recorded.
        </div>
      )}
      {rows.map((r) => (
        <div key={r.id} style={ROW}>
          <span style={{ color: 'var(--hf-ink-2)', flex: '1 1 9rem', minWidth: 0 }}>
            {dateTypeLabel(r.date_type)}
          </span>
          <Mono>{fmtDate(r.date, dateFormat)}</Mono>
          {r.source === 'derived' && (
            <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
              from the standard
            </span>
          )}
          <Badge tone={STATE[r.status].tone} dot={r.status === 'lapsed'}>
            {STATE[r.status].text}
          </Badge>
        </div>
      ))}

      {canEdit && adding && (
        <DisclosedForm opener={opener} style={FORM}>
          {err && <InlineStatus tone="error">{err}</InlineStatus>}
          <div style={FIELDS}>
            <Field label="Date type" style={{ flex: '2 1 12rem' }}>
              {(control) => (
                <Select
                  {...control}
                  value={type}
                  onChange={(v) => setType(v as DateType)}
                  options={DATE_TYPES.map((d) => ({ value: d.id, label: d.label }))}
                />
              )}
            </Field>
            {/* Echoes the day in the org's format: the box reads in the
                browser's order (UI-CONVENTIONS §2). */}
            <DateField
              dense
              label="Date"
              required
              error={dateErr}
              style={{ flex: '1 1 10rem' }}
              value={date}
              onChange={setDate}
            />
          </div>
          <div style={ACTIONS}>
            <Button variant="primary" size="sm" loading={busy} onClick={() => void add()}>
              Save date
            </Button>
            <Button variant="ghost" size="sm" onClick={close}>
              Cancel
            </Button>
          </div>
        </DisclosedForm>
      )}
    </PCard>
  );
}

/** Which field a problem belongs to, so it is said beside that field. */
type EventErrors = { from?: string; to?: string; when?: string; gap?: string };

/** Continuous service — and where it is not continuous, why. */
export function ServiceHistoryCard({
  profileId,
  rows,
  canEdit,
  onChanged,
}: {
  profileId: string;
  rows: EngagementEvent[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const { dateFormat } = useOrgDates();
  const [adding, setAdding] = useState(false);
  const opener = useId();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [when, setWhen] = useState('');
  const [continuous, setContinuous] = useState(true);
  const [gapReason, setGapReason] = useState('');
  const [errors, setErrors] = useState<EventErrors>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setAdding(false);
    setFrom('');
    setTo('');
    setWhen('');
    setContinuous(true);
    setGapReason('');
    setErrors({});
    setErr(null);
  };

  const add = async () => {
    const found: EventErrors = {};
    if (!from.trim()) found.from = 'What they were before.';
    if (!to.trim()) found.to = 'What they became.';
    if (!when) found.when = 'When it took effect.';
    // The database refuses a discontinuity with no reason; saying so here is a
    // better way to learn it than a failed round-trip.
    if (!continuous && !gapReason.trim()) found.gap = 'A break in service has to say why.';
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    setErr(null);
    const { error } = await addEvent({
      profileId,
      fromType: from,
      toType: to,
      effectiveDate: when,
      continuous,
      gapReason,
    });
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    toast.push({ tone: 'success', message: `Recorded ${from.trim()} → ${to.trim()}.` });
    close();
    onChanged();
  };

  return (
    <PCard
      label="Service history"
      right={
        canEdit &&
        !adding && (
          <span id={opener}>
            <Button variant="secondary" size="sm" icon="plus" onClick={() => setAdding(true)}>
              Record change
            </Button>
          </span>
        )
      }
    >
      {!rows.length && <div style={NOTE}>Nothing recorded yet — an unbroken engagement.</div>}
      {rows.map((r) => (
        <div
          key={r.id}
          style={{
            padding: 'var(--hf-space-2) 0',
            borderBottom: '1px solid var(--hf-rule)',
            fontSize: 'var(--hf-text-sm)',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--hf-space-2)',
            }}
          >
            <Mono>{fmtDate(r.effective_date, dateFormat)}</Mono>
            <span style={{ color: 'var(--hf-ink-2)', flex: 1, minWidth: 0 }}>
              {r.from_type} → {r.to_type}
            </span>
            {/* A fact about the record, not something waiting on anyone. */}
            {!r.continuous && <Badge tone="neutral">gap</Badge>}
          </div>
          {!r.continuous && r.gap_reason && (
            <div
              style={{
                fontSize: 'var(--hf-text-xs)',
                color: 'var(--hf-ink-3)',
                marginTop: 'var(--hf-space-1)',
                overflowWrap: 'anywhere',
              }}
            >
              {r.gap_reason}
            </div>
          )}
        </div>
      ))}

      {canEdit && adding && (
        <DisclosedForm opener={opener} style={FORM}>
          {err && <InlineStatus tone="error">{err}</InlineStatus>}
          <div style={FIELDS}>
            <Field label="From" required error={errors.from} style={{ flex: '1 1 9rem' }}>
              <TextInput dense value={from} onChange={setFrom} placeholder="intern" />
            </Field>
            <Field label="To" required error={errors.to} style={{ flex: '1 1 9rem' }}>
              <TextInput dense value={to} onChange={setTo} placeholder="employee" />
            </Field>
            <DateField
              dense
              label="Effective date"
              required
              error={errors.when}
              style={{ flex: '1 1 10rem' }}
              value={when}
              onChange={setWhen}
            />
          </div>
          {/* In a block of its own: as a child of the column it stretched to the
              card's width and centred its label. */}
          <div>
            <Checkbox
              label="There is a break before this"
              checked={!continuous}
              onChange={(broken) => setContinuous(!broken)}
            />
          </div>
          {!continuous && (
            <Field label="Why the break?" required error={errors.gap}>
              <TextInput
                dense
                value={gapReason}
                onChange={setGapReason}
                placeholder="Unpaid sabbatical"
              />
            </Field>
          )}
          <div style={ACTIONS}>
            <Button variant="primary" size="sm" loading={busy} onClick={() => void add()}>
              Save change
            </Button>
            <Button variant="ghost" size="sm" onClick={close}>
              Cancel
            </Button>
          </div>
        </DisclosedForm>
      )}
    </PCard>
  );
}

/**
 * R7.4 — the central list. Rendered with `profileId` on a person's record, and
 * without it as the org-wide log, so a deviation is as visible as the standard.
 */
export function ExceptionsCard({
  rows,
  compact,
  period,
}: {
  rows: HrException[];
  compact?: boolean;
  /** The org-wide list steps a month at a time; with it, an empty month says so. */
  period?: { label: string; onPrev: () => void; onNext: () => void; nextDisabled: boolean };
}) {
  const { tz, dateFormat } = useOrgDates();
  if (!rows.length && !period) return null;
  return (
    <PCard
      label={compact ? 'Exceptions on this record' : 'Exceptions'}
      right={
        period && (
          <PeriodStepper
            label={period.label}
            unit="month"
            labelWidth="8.5rem"
            onPrev={period.onPrev}
            onNext={period.onNext}
            nextDisabled={period.nextDisabled}
          />
        )
      }
    >
      {!compact && (
        <p style={{ ...NOTE, margin: '0 0 var(--hf-space-2)' }}>
          Every deviation from a standard, with the reason given for it.
        </p>
      )}
      {!rows.length && period && <div style={NOTE}>No exceptions in {period.label}.</div>}
      {rows.map((r) => (
        <div
          key={r.id}
          style={{
            padding: 'var(--hf-space-2) 0',
            borderBottom: '1px solid var(--hf-rule)',
            fontSize: 'var(--hf-text-sm)',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--hf-space-2)',
            }}
          >
            {/* A record of what happened: neutral. Amber would say it is waiting. */}
            <Badge tone="neutral">{r.subject_type.replace(/_/g, ' ')}</Badge>
            <span style={{ color: 'var(--hf-ink-2)', flex: 1, minWidth: 0 }}>{r.action}</span>
            {/* The org's day: an exception filed late in the evening is still today's. */}
            <Mono dim>{fmtDate(r.created_at, dateFormat, { tz })}</Mono>
          </div>
          {(r.standard_value || r.actual_value) && (
            <div
              style={{
                fontSize: 'var(--hf-text-xs)',
                color: 'var(--hf-ink-3)',
                marginTop: 'var(--hf-space-1)',
              }}
            >
              standard <Mono>{r.standard_value ?? '—'}</Mono> · actual{' '}
              <Mono>{r.actual_value ?? '—'}</Mono>
            </div>
          )}
          <div
            style={{
              fontSize: 'var(--hf-text-xs)',
              color: 'var(--hf-ink-3)',
              marginTop: 'var(--hf-space-1)',
              overflowWrap: 'anywhere',
            }}
          >
            {r.reason}
          </div>
        </div>
      ))}
    </PCard>
  );
}
